import { Divider, Drawer, IconButton, Toolbar, Typography } from '@mui/material';
import styled from 'styled-components';

interface ExpandableProps {
  $expanded: boolean;
}

// Layout stays fixed across states so nothing snaps on collapse: the logo (left)
// fades + clips while the toggle (right) slides in as the drawer narrows.
const TitleToolbar = styled(Toolbar)<ExpandableProps>`
  && {
    height: ${({ theme }) => theme.shape.appbarHeight}px;
    min-height: ${({ theme }) => theme.shape.appbarHeight}px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* Symmetric 8px insets centre the 48px toggle within the 64px rail
       (8 + 48 + 8) when collapsed, aligning it with the nav item column, and keep
       it near the right edge when expanded. */
    padding-left: ${({ theme }) => theme.spacing(1)};
    padding-right: ${({ theme }) => theme.spacing(1)};
    overflow: hidden;
  }
`;

// Shaded, rounded-rectangle icon button (not a circular IconButton). Sized as a
// 48px square so it shares an identical footprint with the rail nav items.
const ToggleButton = styled(IconButton)`
  && {
    flex-shrink: 0;
    width: ${({ theme }) => theme.spacing(6)};
    height: ${({ theme }) => theme.spacing(6)};
    padding: 0;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    background-color: ${({ theme }) => theme.palette.backdrop.main};
    color: ${({ theme }) => theme.palette.gray.main};
    &:hover {
      background-color: ${({ theme }) => theme.palette.backdrop.dark};
    }
  }
`;

interface StyledDrawerProps extends ExpandableProps {
  $isSm: boolean;
}

const StyledDrawer = styled(Drawer)<StyledDrawerProps>`
  && {
    & .MuiDrawer-paper {
      box-sizing: border-box;
      border: none;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      /* Inset top/bottom by the same gap the content panel has (spacing(2)), so the
         drawer's logo and profile footer line up with where the content starts/ends. */
      padding-top: ${({ theme }) => theme.spacing(2)};
      padding-bottom: ${({ theme }) => theme.spacing(2)};
      width: ${({ theme, $expanded, $isSm }) =>
        !$isSm || $expanded ? theme.shape.drawerWidth : theme.shape.drawerWidthRail}px;
      background-color: ${({ theme }) => theme.palette.background.default};
      background-image: none;
      transition: ${({ theme }) =>
        theme.transitions.create('width', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        })};
    }
  }
`;

// Scrollable but with the scrollbar visually hidden across browsers.
const ScrollableContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// Separates the nav list from the profile footer pinned at the bottom.
const FooterDivider = styled(Divider)`
  && {
    margin: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
    border-color: ${({ theme }) => theme.palette.divider};
  }
`;

const GradientEmphasis = styled.span`
  font-weight: bold;
  background: -webkit-linear-gradient(
    45deg,
    ${({ theme }) => theme.palette.turquoise.main},
    ${({ theme }) => theme.palette.blue.main}
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Title = styled(Typography)<ExpandableProps>`
  && {
    pointer-events: ${({ $expanded }) => ($expanded ? 'all' : 'none')};
    display: flex;
    align-items: center;
    /* Allow the logo to shrink to zero width so the TitleToolbar (overflow: hidden)
       clips it on collapse. No overflow here, so glyph descenders aren't cut. */
    min-width: 0;
    /* Left inset only while expanded — it collapses to 0 in the rail so it stops
       occupying space and the toggle can centre. Animated alongside the fade. */
    margin-left: ${({ theme, $expanded }) => ($expanded ? theme.spacing(2) : 0)};
    color: ${({ theme }) => theme.palette.text.primary};
    user-select: none;
    line-height: 1.2;
    cursor: pointer;
    white-space: nowrap;
    /* Fade (not unmount) on collapse so the logo dissolves while the drawer narrows
       and clips it away, instead of vanishing in a single frame. */
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: ${({ theme }) =>
      theme.transitions.create(['opacity', 'margin-left'], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      })};
  }
`;

export {
  FooterDivider,
  GradientEmphasis,
  ScrollableContainer,
  StyledDrawer,
  Title,
  TitleToolbar,
  ToggleButton,
};
